import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../components/shared/Input';
import Button from '../components/shared/Button';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../api/admin';
import { BASE_URL, type ApiError } from '../api/client';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'VR Companion – Login';
    return () => { document.title = 'VR Companion'; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.email.trim() || !formData.password) {
      setError('Please enter both email and password');
      return;
    }
    setLoading(true);
    const TIMEOUT_MS = 15000;
    try {
      const loginPromise = adminApi.login({
        email: formData.email.trim(),
        password: formData.password,
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Connection timed out. Is the backend running at ' + (process.env.REACT_APP_API_URL || 'http://192.168.1.90:4000') + '?')), TIMEOUT_MS)
      );
      const res = await Promise.race([loginPromise, timeoutPromise]);
      setUser(res.user);
      localStorage.setItem('adminToken', res.token);
      localStorage.setItem('isAuthenticated', 'true');
      if (res.user.role === 'operator') {
        navigate('/control-panel');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      const rawMessage = apiErr?.message || String(err) || 'Login failed.';
      const message =
        apiErr.status === 401
          ? 'Incorrect email or password'
          : rawMessage;
      const isNetworkError =
        /timed out|fetch|Connection refused|Unable to resolve|Network request failed|Failed to connect/i.test(message);
      if (isNetworkError) {
        setError(
          `${message} — Backend: ${BASE_URL}. Test in this phone's browser: ${BASE_URL}/health. Same Wi‑Fi as PC? Backend running (npm run dev)?`
        );
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h2 className="login-title">
            VR Companion
          </h2>
          <p className="login-subtitle">
            Sign in to your account
          </p>
        </div>
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-form-fields space-y-4">
            <Input
              type="email"
              label="Email address"
              placeholder="admin@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              type="password"
              label="Password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          {error && (
            <div className="error-alert">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="login-options">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="login-checkbox"
              />
              <label htmlFor="remember-me" className="login-checkbox-label">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button type="button" className="login-link">
                Forgot password?
              </button>
            </div>
          </div>

          <div>
            <Button type="submit" className="btn-full" size="lg" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
